import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
    hasNewVersion,
    getNewVersionNotes,
    markCurrentVersionAsSeen,
    getCurrentAppVersion,
} from '@/services/releaseNotes'
import { ReleaseNote } from '@/types/releaseNotes'
import { formatDateWithFormatter } from '@/utils/function'
import { useTourLauncher } from '@/hooks/useTourLauncher'
import {
    Rocket,
    Sparkles,
    Bug,
    Wrench,
    Shield,
    ChevronRight,
    X,
    PartyPopper,
    PlayCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const WHATS_NEW_DISMISSED_KEY = 'datacentral_whats_new_dismissed'

const WhatsNewModal = () => {
    const { t } = useTranslation('release-notes')
    const navigate = useNavigate()
    const { startFirstTourForVersion } = useTourLauncher({
        onComplete: () => {
            markCurrentVersionAsSeen()
        },
    })
    const [isOpen, setIsOpen] = useState(false)
    const [newNotes, setNewNotes] = useState<ReleaseNote[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)

    useEffect(() => {
        // Check if there's a new version
        if (!hasNewVersion()) {
            return
        }

        // Check if modal was already dismissed in this session
        const currentVersion = getCurrentAppVersion()
        const dismissedVersion = sessionStorage.getItem(WHATS_NEW_DISMISSED_KEY)

        if (dismissedVersion === currentVersion) {
            return
        }

        // Get notes for new versions
        const notes = getNewVersionNotes()
        if (notes.length > 0) {
            setNewNotes(notes)
            // Delay to show after page load
            setTimeout(() => setIsOpen(true), 1000)
        }
    }, [])

    const handleDismiss = () => {
        const currentVersion = getCurrentAppVersion()
        sessionStorage.setItem(WHATS_NEW_DISMISSED_KEY, currentVersion)
        setIsOpen(false)
    }

    const handleMarkAsSeen = () => {
        markCurrentVersionAsSeen()
        setIsOpen(false)
    }

    const handleViewAll = () => {
        handleMarkAsSeen()
        navigate('/release-notes')
    }

    const handleStartTour = () => {
        setIsOpen(false)
        startFirstTourForVersion(currentNote.version)
    }

    const getCategoryIcon = (category: ReleaseNote['category']) => {
        const iconClass = 'h-5 w-5'
        switch (category) {
            case 'feature':
                return <Sparkles className={`${iconClass} text-emerald-500`} />
            case 'bugfix':
                return <Bug className={`${iconClass} text-red-500`} />
            case 'improvement':
                return <Wrench className={`${iconClass} text-blue-500`} />
            case 'security':
                return <Shield className={`${iconClass} text-orange-500`} />
            default:
                return <Rocket className={iconClass} />
        }
    }

    const getCategoryColor = (category: ReleaseNote['category']) => {
        switch (category) {
            case 'feature':
                return 'from-emerald-500 to-teal-600'
            case 'bugfix':
                return 'from-red-500 to-rose-600'
            case 'improvement':
                return 'from-blue-500 to-indigo-600'
            case 'security':
                return 'from-orange-500 to-amber-600'
            default:
                return 'from-primary to-primary/80'
        }
    }

    if (newNotes.length === 0) return null

    const currentNote = newNotes[currentIndex]
    const hasMultiple = newNotes.length > 1

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
                {/* Header with gradient */}
                <div
                    className={`bg-gradient-to-br ${getCategoryColor(currentNote.category)} p-6 text-white relative`}
                >
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
                        onClick={handleDismiss}
                    >
                        <X className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                            <PartyPopper className="h-6 w-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                {t('whats-new.title')}
                                <Badge className="bg-white/20 text-white border-0 text-xs">
                                    v{currentNote.version}
                                </Badge>
                            </h2>
                            <p className="text-white/80 text-sm">
                                {formatDateWithFormatter(
                                    currentNote.date,
                                    'DD/MM/YYYY'
                                )}
                            </p>
                        </div>
                    </div>

                    <h3 className="text-lg font-semibold leading-tight">
                        {currentNote.title}
                    </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            {getCategoryIcon(currentNote.category)}
                        </div>
                        <div>
                            <Badge variant="outline" className="mb-2 text-xs">
                                {t(`category.${currentNote.category}`)}
                            </Badge>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {currentNote.summary}
                            </p>
                        </div>
                    </div>

                    {/* Pagination dots for multiple notes */}
                    {hasMultiple && (
                        <div className="flex items-center justify-center gap-2 py-3 border-t border-b mb-4">
                            {newNotes.slice(0, 5).map((_, idx) => (
                                <button
                                    key={idx}
                                    className={`h-2 rounded-full transition-all ${
                                        idx === currentIndex
                                            ? 'w-6 bg-primary'
                                            : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                                    }`}
                                    onClick={() => setCurrentIndex(idx)}
                                />
                            ))}
                            {newNotes.length > 5 && (
                                <span className="text-xs text-muted-foreground ml-1">
                                    +{newNotes.length - 5}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={handleDismiss}
                        >
                            {t('whats-new.remind-later')}
                        </Button>
                        {currentNote.hasTour ? (
                            <Button
                                className="flex-1 gap-1"
                                onClick={handleStartTour}
                            >
                                <PlayCircle className="h-4 w-4" />
                                {t('whats-new.start-tour')}
                            </Button>
                        ) : (
                            <Button
                                className="flex-1 gap-1"
                                onClick={handleViewAll}
                            >
                                {t('whats-new.view-details')}
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        )}
                    </div>

                    {/* View all link when tour is available */}
                    {currentNote.hasTour && (
                        <button
                            className="w-full text-center text-xs text-muted-foreground mt-3 hover:text-primary underline-offset-4 hover:underline"
                            onClick={handleViewAll}
                        >
                            {t('whats-new.view-details')}
                        </button>
                    )}

                    {newNotes.length > 1 && (
                        <p className="text-center text-xs text-muted-foreground mt-3">
                            {t('whats-new.more-updates', {
                                count: newNotes.length - 1,
                            })}
                        </p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default WhatsNewModal
